import type { PDFFont } from "pdf-lib";

export interface TextLine {
  text: string;
  width: number;
}

export interface TextLayoutResult {
  lines: TextLine[];
  fontSize: number;
  totalHeight: number;
  lineHeight: number;
  startY: number;
}

export interface TextMeasurer {
  widthOfText(text: string, fontSize: number): number;
  heightAtSize(fontSize: number): number;
}

export interface CalculateTextLayoutOptions {
  text: string;
  measurer: TextMeasurer;
  initialFontSize: number;
  containerWidth: number;
  containerHeight: number;
  containerY: number;
  minFontSize?: number;
  fontSizeStep?: number;
  maxIterations?: number;
  lineHeightMultiplier?: number;
}

export const DEFAULT_MIN_FONT_SIZE = 6;
export const DEFAULT_FONT_STEP = 1;

export function wrapTextToLines(
  text: string,
  measurer: TextMeasurer,
  fontSize: number,
  maxWidth: number,
): TextLine[] {
  const words = text.split(" ");
  const lines: TextLine[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = measurer.widthOfText(testLine, fontSize);

    if (width <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        const lineWidth = measurer.widthOfText(currentLine, fontSize);
        lines.push({ text: currentLine, width: lineWidth });
        currentLine = word;
      } else {
        if (word.length > 1) {
          let charLine = "";
          for (const char of word) {
            const testCharLine = charLine + char;
            const charWidth = measurer.widthOfText(testCharLine, fontSize);
            if (charWidth <= maxWidth) {
              charLine = testCharLine;
            } else {
              if (charLine) {
                const lineWidth = measurer.widthOfText(charLine, fontSize);
                lines.push({ text: charLine, width: lineWidth });
              }
              charLine = char;
            }
          }
          if (charLine) {
            const lineWidth = measurer.widthOfText(charLine, fontSize);
            lines.push({ text: charLine, width: lineWidth });
          }
          currentLine = "";
        } else {
          const lineWidth = measurer.widthOfText(word, fontSize);
          lines.push({ text: word, width: lineWidth });
          currentLine = "";
        }
      }
    }
  }

  if (currentLine) {
    const lineWidth = measurer.widthOfText(currentLine, fontSize);
    lines.push({ text: currentLine, width: lineWidth });
  }

  return lines;
}

export function calculateOptimalLayout({
  text,
  measurer,
  initialFontSize,
  containerWidth,
  containerHeight,
  containerY,
  minFontSize = DEFAULT_MIN_FONT_SIZE,
  fontSizeStep = DEFAULT_FONT_STEP,
  maxIterations = 10,
  lineHeightMultiplier = 1,
}: CalculateTextLayoutOptions): TextLayoutResult {
  let fontSize = initialFontSize;
  let iterationAttempts = 0;

  const buildResult = (lines: TextLine[], size: number): TextLayoutResult => {
    const baseHeight = measurer.heightAtSize(size);
    const lineHeight = baseHeight * lineHeightMultiplier;
    const totalHeight = lines.length * lineHeight;
    const bottomBaseline = containerY + Math.min(lineHeight, containerHeight);
    const startY = bottomBaseline + (lines.length - 1) * lineHeight;

    return {
      lines,
      fontSize: size,
      totalHeight,
      lineHeight,
      startY,
    };
  };

  while (fontSize > minFontSize) {
    const lines = wrapTextToLines(text, measurer, fontSize, containerWidth);
    const result = buildResult(lines, fontSize);

    if (result.totalHeight <= containerHeight) {
      return result;
    }

    fontSize = Math.max(fontSize - fontSizeStep, minFontSize);

    if (iterationAttempts > maxIterations) {
      break;
    }
    iterationAttempts += 1;
  }

  const fallbackLines = wrapTextToLines(text, measurer, minFontSize, containerWidth);
  return buildResult(fallbackLines, minFontSize);
}

export function createPdfTextMeasurer(font: PDFFont): TextMeasurer {
  return {
    widthOfText: (text, fontSize) => font.widthOfTextAtSize(text, fontSize),
    heightAtSize: (fontSize) => font.heightAtSize(fontSize),
  };
}

